import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  ArrowRight,
  MapPin,
  Globe,
  Mail,
  Sparkles,
  Building2,
  UserCircle2,
  Loader2,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";
import Header from "@/components/politics/Header";
import Footer from "@/components/politics/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIER = {
  founding: { text: "Parceiro Fundador", color: "#7E20FF" },
  certified: { text: "Parceiro Certificado", color: "#208BFF" },
  operator: { text: "Operador RSM Politics", color: "#3ED383" },
};

const TYPE = {
  agencia: { text: "Agência", icon: Building2 },
  consultor: { text: "Consultor independente", icon: UserCircle2 },
};

export default function PartnerProfile() {
  const { slug } = useParams();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/partners/${slug}`)
      .then((res) => mounted && setPartner(res.data))
      .catch((err) => {
        if (mounted) {
          setError(
            err?.response?.status === 404
              ? "Parceiro não encontrado."
              : "Não foi possível carregar este perfil agora.",
          );
        }
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <Shell>
        <div className="flex items-center justify-center py-32 text-[#7D8188]">
          <Loader2 size={22} className="animate-spin" />
        </div>
      </Shell>
    );
  }

  if (error || !partner) {
    return (
      <Shell>
        <section className="mx-auto max-w-3xl px-6 py-32 text-center">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[#D94A0A]/10 text-[#D94A0A]">
            <AlertTriangle size={22} />
          </div>
          <h1 className="mt-6 font-display text-3xl font-bold tracking-tight text-[#1B1D1D]">
            {error}
          </h1>
          <Link
            to="/politics/parceiros"
            data-testid="partner-profile-back-not-found"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#1B1D1D] px-6 py-3 text-sm font-medium text-white hover:bg-[#7E20FF] transition-colors"
          >
            <ArrowLeft size={14} /> Voltar ao diretório
          </Link>
        </section>
      </Shell>
    );
  }

  const tier = TIER[partner.tier] || TIER.certified;
  const typeInfo = TYPE[partner.type] || TYPE.agencia;
  const TypeIcon = typeInfo.icon;
  const initials = partner.name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <Shell>
      {/* Hero */}
      <section
        data-testid="partner-profile-hero"
        className="relative isolate overflow-hidden pt-32 pb-16 sm:pt-40 sm:pb-20"
      >
        <div className="absolute inset-0 -z-10 rsm-grid-bg opacity-90" />
        <div
          className="absolute -top-24 right-[-10%] -z-10 h-[400px] w-[400px] rounded-full blur-3xl opacity-25"
          style={{
            background: `radial-gradient(closest-side, ${tier.color}, transparent 70%)`,
          }}
        />

        <div className="mx-auto max-w-7xl px-6">
          <Link
            to="/politics/parceiros"
            data-testid="partner-profile-back"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#51565F] hover:text-[#7E20FF] transition-colors"
          >
            <ArrowLeft size={14} /> Diretório de parceiros
          </Link>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-8">
              <div className="flex items-start gap-5">
                {partner.logo_url ? (
                  <img
                    src={partner.logo_url}
                    alt={`${partner.name} logo`}
                    className="h-20 w-20 rounded-2xl object-contain bg-white p-3 border border-[#1B1D1D]/10"
                  />
                ) : (
                  <div
                    className="grid h-20 w-20 place-items-center rounded-2xl bg-white border border-[#1B1D1D]/10 font-display text-2xl font-bold"
                    style={{ color: tier.color }}
                  >
                    {initials}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.2em] uppercase text-[#7D8188]">
                    <TypeIcon size={12} />
                    {typeInfo.text}
                  </div>
                  <h1
                    data-testid="partner-profile-name"
                    className="mt-2 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[1] text-[#1B1D1D]"
                  >
                    {partner.name}
                  </h1>
                  <span
                    className="mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold tracking-[0.18em] uppercase"
                    style={{
                      color: tier.color,
                      backgroundColor: `${tier.color}15`,
                    }}
                  >
                    <Sparkles size={11} /> {tier.text}
                  </span>
                  <ProfileAvailabilityBadge availability={partner.availability} />
                </div>
              </div>

              {partner.tagline && (
                <p
                  data-testid="partner-profile-tagline"
                  className="mt-8 text-xl sm:text-2xl leading-snug text-[#1B1D1D] font-display max-w-2xl"
                >
                  {partner.tagline}
                </p>
              )}

              {partner.description && (
                <p className="mt-6 text-base leading-relaxed text-[#51565F] max-w-2xl whitespace-pre-line">
                  {partner.description}
                </p>
              )}

              {partner.is_placeholder && (
                <div
                  data-testid="partner-profile-placeholder"
                  className="mt-8 inline-flex items-start gap-2 rounded-xl border border-[#FA9F22]/30 bg-[#FA9F22]/10 px-4 py-3 text-xs text-[#1B1D1D] max-w-xl"
                >
                  <AlertTriangle size={14} className="mt-0.5 text-[#FA9F22] flex-shrink-0" />
                  <span>
                    Perfil ilustrativo da leva fundadora. Será substituído por um
                    parceiro real à medida que o programa formaliza as primeiras
                    certificações.
                  </span>
                </div>
              )}
            </div>

            <aside className="lg:col-span-4 lg:pl-6">
              <div className="rounded-2xl border border-[#1B1D1D]/10 bg-white p-6">
                <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#7D8188]">
                  Contato e localização
                </span>
                <div className="mt-4 space-y-3 text-sm">
                  {(partner.city || partner.state) && (
                    <Row icon={MapPin}>
                      {[partner.city, partner.state].filter(Boolean).join(" · ")}
                    </Row>
                  )}
                  {partner.website && (
                    <Row icon={Globe}>
                      <a
                        href={partner.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="partner-profile-website"
                        className="text-[#208BFF] hover:underline break-all"
                      >
                        {partner.website.replace(/^https?:\/\//, "")}
                      </a>
                    </Row>
                  )}
                  {partner.contact_email && (
                    <Row icon={Mail}>
                      <a
                        href={`mailto:${partner.contact_email}`}
                        data-testid="partner-profile-email"
                        className="text-[#208BFF] hover:underline break-all"
                      >
                        {partner.contact_email}
                      </a>
                    </Row>
                  )}
                </div>

                <div className="mt-6 pt-5 border-t border-[#1B1D1D]/8 space-y-2">
                  <Link
                    to="/politics#contato"
                    data-testid="partner-profile-cta-qg"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#1B1D1D] px-5 py-3 text-sm font-semibold text-white hover:bg-[#208BFF] transition-colors"
                  >
                    Sou QG e quero contratar
                    <ArrowRight size={14} />
                  </Link>
                  <Link
                    to="/politics/agencies#parceria"
                    data-testid="partner-profile-cta-partner"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#1B1D1D]/15 px-5 py-3 text-sm font-medium text-[#1B1D1D] hover:border-[#7E20FF] hover:text-[#7E20FF] transition-colors"
                  >
                    Quero ser parceiro também
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Specialties */}
      {partner.specialties?.length > 0 && (
        <section
          data-testid="partner-profile-specialties"
          className="relative py-16 border-t border-[#1B1D1D]/8 bg-white"
        >
          <div className="mx-auto max-w-7xl px-6">
            <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#208BFF]">
              Áreas de atuação
            </span>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#1B1D1D]">
              Onde {partner.name.split(" ")[0]} entrega valor
            </h2>
            <div className="mt-8 flex flex-wrap gap-2">
              {partner.specialties.map((s) => (
                <span
                  key={s}
                  className="inline-flex items-center gap-2 rounded-full border border-[#1B1D1D]/10 bg-[#F5FAFB] px-4 py-2 text-sm font-medium text-[#1B1D1D]"
                >
                  <CheckCircle2 size={14} className="text-[#3ED383]" />
                  {s}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Bottom CTA back */}
      <section className="relative py-16 sm:py-20 bg-[#F5FAFB] border-t border-[#1B1D1D]/8">
        <div className="mx-auto max-w-5xl px-6 text-center">
          <Link
            to="/politics/parceiros"
            data-testid="partner-profile-back-bottom"
            className="inline-flex items-center gap-2 rounded-full border border-[#1B1D1D]/15 bg-white px-6 py-3 text-sm font-medium text-[#1B1D1D] hover:border-[#7E20FF] hover:text-[#7E20FF] transition-colors"
          >
            <ArrowLeft size={14} /> Ver todos os parceiros
          </Link>
        </div>
      </section>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <main
      data-testid="partner-profile-page"
      className="min-h-screen bg-[#F5FAFB] text-[#1B1D1D]"
    >
      <Header />
      {children}
      <Footer />
    </main>
  );
}

function Row({ icon: Icon, children }) {
  return (
    <div className="flex items-start gap-2.5 text-[#1B1D1D]">
      <Icon size={14} className="mt-0.5 text-[#7D8188] flex-shrink-0" />
      <span>{children}</span>
    </div>
  );
}

function ProfileAvailabilityBadge({ availability }) {
  const accepting = (availability || "accepting") === "accepting";
  const color = accepting ? "#3ED383" : "#7D8188";
  const text = accepting ? "Aceitando projetos" : "Agenda lotada";
  return (
    <span
      data-testid="partner-profile-availability"
      className="ml-2 mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold tracking-[0.18em] uppercase align-middle"
      style={{ color, backgroundColor: `${color}15` }}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${accepting ? "animate-pulse" : ""}`}
        style={{ background: color }}
      />
      {text}
    </span>
  );
}
